import React from "react";
//import ReactDom from 'react-dom/client';
import {
    BrowserRouter,
    createBrowserRouter,
    RouterProvider,
    Navigate,
} from "react-router-dom";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { store } from "./app/store";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { orange } from "@mui/material/colors";

// date-picker
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import type {} from "@mui/x-date-pickers/themeAugmentation";

//
// App
//
//import { useAuth } from "./features/auth/hooks";
import AppInit from "./AppInit";
import AppSpinner from "./features/app-spinner/AppSpinner";
import ErrorPage from "./error-page";
import { Home } from "./home/page";
import { Counter } from "./features/counter/Counter";
import { User } from "./features/auth/user/User";
import { Settings } from "./features/settings/Settings";
import { Login } from "./features/auth/session/Login";
import { NewUserForm } from "./features/auth/session/NewUserForm";
import { Profile } from "./features/auth/session/Profile";
import { SessionDebug } from "./features/auth/session/Debug";
import { Authorized } from "./features/auth/session/Authorized";
import { MapWrapper } from "./features/map/Map";
import { Style } from "./features/debug/Style";
import Contact from "./features/contact/Contact";
import ContactList from "./features/contact/ContactList";
import FileLayout from "./features/file/FileLayout";
import File from "./features/file/File";
import FileEdit from "./features/file/Edit";
import FileByFilter from "./features/file/FileByFilter";
import { PageBrowser } from "./features/page/PageBrowser";
import { Filter } from "./features/filter/Filter";
import FilterView from "./features/filter/FilterView";
import { PageViewer } from "./features/page/PageViewer";
import { PageEdit } from "./features/page/PageEdit";
import { PageList } from "./features/page/PageList";
import { TicketBrowser } from "./features/ticket/TicketBrowser";
import { TicketViewer } from "./features/ticket/TicketViewer";
import { TicketEdit } from "./features/ticket/TicketEdit";
import { TicketByDate } from "./features/ticket/TicketByDate";
import { TicketByContext } from "./features/ticket/TicketByContext";
import TicketByDelTag from "./features/ticket/TicketByDelTag";
import { Note } from "./features/note/Note";
import reportWebVitals from "./reportWebVitals";
import "./index.css";

// Theme

declare module "@mui/material/styles" {
    interface Theme {
        status: {
            danger: string;
        };
    }
    // allow configuration using `createTheme`
    interface ThemeOptions {
        status?: {
            danger?: string;
        };
    }
    interface TypographyVariants {
        title: React.CSSProperties;
    }
    interface TypographyVariantsOptions {
        title?: React.CSSProperties;
    }
}

declare module "@mui/material/Typography" {
    interface TypographyPropsVariantOverrides {
        title: true;
    }
}

const theme = createTheme({
    status: {
        danger: orange[500],
    },
    palette: {
        primary: {
            main: "#003366", // nice kind of red: '#a42f2f',
        },
        action: {
            active: "#212529",
            //hover: '#000088',
        },
    },
    mixins: {
        toolbar: { minHeight: 48, bgcolor: "#854ebf" },
    },
    components: {
        MuiCard: {
            styleOverrides: {
                root: {
                    maxWidth: 1200,
                    margin: 25,
                },
            },
        },
        MuiDatePicker: {
            /*
styleOverrides: {
root: {
backgroundColor: 'red',
},
},
		// */
        },
        /*
		MuiTypography: {
			defaultProps: {
				variantMapping: {
					title: 'h3',
				},
			},
		},
		// */
    },
    typography: {
        title: {
            fontSize: "1.1rem",
            color: "#333333",
        },
        h1: {
            fontSize: "0.5rem",
        },
        h2: {
            fontSize: "1.4rem",
        },
        button: { color: "#00f0f0" },
        //title: { fontSize: '1.1rem' },
    },
});

// Router

/*
const ProtectedRoute = ({ children }: any) => {
	const { user } = useAuth();
	if (!user) {
		return <Navigate to="/" />;
	}
	return children;
};
// */

const container = document.getElementById("root")!;
const root = createRoot(container);
const router = createBrowserRouter([
    {
        path: "/",
        //element: <div>Hello world!</div>,
        element: <AppInit />,
        errorElement: <ErrorPage />,
        children: [
            {
                path: "",
                element: <Home />,
            },
            {
                path: "counter",
                element: <Counter />,
            },
            {
                path: "session/debug",
                element: <SessionDebug />,
            },
            {
                path: "login",
                element: <Login />,
            },
            {
                path: "profile",
                element: <Profile />,
            },
            {
                path: "user",
                element: <User />,
            },
            {
                path: "auth/authorized",
                element: <Authorized />,
            },
            {
                path: "auth/new-user-form",
                element: <NewUserForm />,
            },
            {
                path: "note",
                element: <Note />,
            },
            {
                path: "contact/",
                element: <ContactList />,
            },
            {
                path: "contact/view/:id",
                element: <Contact />,
            },
            {
                path: "filter/view/:id",
                element: <FilterView />,
            },
            {
                path: "filter/",
                element: <Filter />,
                children: [
                    /*{
                        path: "view/:pageId",
                        element: <PageViewer />,
                        },// */
                    {
                        path: "edit/",
                        element: <PageEdit />,
                    },
                    {
                        path: "edit/:pageId",
                        element: <PageEdit />,
                    },
                    {
                        path: "list",
                        element: <PageList />,
                    },
                ],
            },

            {
                path: "page/",
                element: <PageBrowser />,
                children: [
                    {
                        path: "view/:pageId",
                        element: <PageViewer />,
                    },
                    {
                        path: "edit/",
                        element: <PageEdit />,
                    },
                    {
                        path: "edit/:pageId",
                        element: <PageEdit />,
                    },
                    {
                        path: "list",
                        element: <PageList />,
                    },
                ],
            },
            {
                path: "file/",
                element: <FileLayout />,
                children: [
                    {
                        path: "view/:id",
                        element: <File />,
                    },
                    {
                        path: "by-filter/",
                        element: <FileByFilter />,
                    },
                    {
                        path: "edit/",
                        element: <FileEdit />,
                    },
                    {
                        path: "edit/:id",
                        element: <FileEdit />,
                    },
                ],
            },
            {
                path: "calendar/",
                element: <TicketBrowser />,
                children: [
                    {
                        path: "",
                        element: <TicketViewer />,
                    },
                    {
                        path: "view/",
                        element: <TicketViewer />,
                    },
                    {
                        path: "view/:id",
                        element: <TicketViewer />,
                    },
                ],
            },
            {
                path: "ticket/",
                element: <TicketBrowser />,
                children: [
                    {
                        path: "",
                        element: <TicketViewer />,
                    },
                    {
                        path: "view/",
                        element: <TicketViewer />,
                    },
                    {
                        path: "view/:id",
                        element: <TicketViewer />,
                    },
                    {
                        path: "bydate/",
                        element: <TicketByDate />,
                    },
                    {
                        path: "bydate/:datum",
                        element: <TicketByDate />,
                    },
                    {
                        path: "bydate/:datum/:id",
                        element: <TicketByDate />,
                    },
                    {
                        path: "byContext/",
                        element: <TicketByContext />,
                    },
                    {
                        path: "byContext/:context_id",
                        element: <TicketByContext />,
                    },
                    {
                        path: "byContext/:context_id/:id",
                        element: <TicketByContext />,
                    },
                    {
                        path: "byDelTag/",
                        element: <TicketByDelTag />,
                    },
                    {
                        path: "byDelTag/:tag",
                        element: <TicketByDelTag />,
                    },
                    {
                        path: "edit/",
                        element: <TicketEdit />,
                    },
                    {
                        path: "edit/:id",
                        element: <TicketEdit />,
                    },
                ],
            },
            {
                path: "map",
                element: <MapWrapper />,
            },
            {
                path: "settings",
                element: <Settings />,
            },
            {
                path: "debug/style",
                element: <Style />,
            },
        ],
    },
    {
        path: "/init",
        element: <AppSpinner />,
    },
]);

root.render(
    <React.StrictMode>
        <Provider store={store}>
            <ThemeProvider theme={theme}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <RouterProvider router={router} />
                </LocalizationProvider>
            </ThemeProvider>
        </Provider>
    </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
